import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pozdravlenie-v-stihah")!;

const faqItems = [
  { q: "Чем поздравление в стихах с музыкой отличается от обычного?", a: "Обычные стихи читают и забывают. Музыкальное поздравление — полноценная песня с вокалом, мелодией и вашим текстом." },
  { q: "Нужно ли самому писать стихи?", a: "Нет. Вы рассказываете историю — бот сам создаст поэтический текст и положит на музыку." },
  { q: "На какой праздник можно заказать?", a: "На любой: день рождения, юбилей, свадьба, 8 Марта, Новый год — или просто так." },
  { q: "Можно ли выбрать стиль музыки?", a: "Да. Баллада, поп, рэп, рок, джаз, частушки — вы выбираете стиль." },
  { q: "Сколько стоит музыкальное поздравление?", a: "Демоверсия бесплатна. Полная версия — от 249 ₽." },
];

const PozdravlenieVStihah = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">Поздравление в стихах с музыкой</h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Вы хотите поздравить — но не открыткой из интернета. Вы хотите, чтобы поздравление <strong className="text-foreground">попало в сердце</strong>.</p>
            <p>Ваши слова звучат в <strong className="text-foreground">настоящей песне</strong>. С музыкой, с вокалом, с мелодией. Это живое поздравление, которое можно включить, переслать, поставить на повтор.</p>
            <p><strong className="text-foreground">Музыкальное поздравление в стихах</strong> — следующий уровень после открытки. Три минуты — и у вас готов подарок.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему музыкальное поздравление сильнее обычного</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Стихи на бумаге — текст. Стихи в песне — эмоция. Открытку прочитают один раз. Песню включат снова.</p>
              <p>Главное — это личное. Конкретная история конкретного человека. Подробнее — <Link to="/pesnya-v-podarok" className="text-primary hover:underline">песня в подарок</Link>.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">✍️ Создать поздравление в стихах с музыкой</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Готово за 3 минуты.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">На какой праздник заказать музыкальное поздравление</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground"><Link to="/pesnya-na-den-rozhdeniya" className="text-primary hover:underline">День рождения</Link>.</strong> Вместо тоста — включите песню.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-na-yubiley" className="text-primary hover:underline">Юбилей</Link>.</strong> Целая жизнь в трёх минутах музыки.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-na-svadbu" className="text-primary hover:underline">Свадьба</Link> или годовщина.</strong> Поздравление в формате песни с вашей историей.</p>
              <p><strong className="text-foreground">8 Марта, 23 февраля, Новый год.</strong> Когда хочется поздравить не шаблонно.</p>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Все читали стихи. А я — включила песню. И выиграла вечер»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>На юбилее свекрови невестка Ольга включила персональную песню вместо тоста. Зал затих. Свекровь обняла Ольгу: «Это лучший подарок за все мои шестьдесят лет». После этого все остальные поздравления казались обычными.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Создать поздравление, которое запомнится</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Частые вопросы о поздравлениях в стихах</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Пусть ваши слова зазвучат в музыке</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Поздравление, которое невозможно забыть.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PozdravlenieVStihah;
